import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

import { playStoreUrl } from '#features/app-install';
import { AppleGlyph, GooglePlayGlyph } from '../storeGlyphs';

// Both stores share one pill shape/size/tone. Available store is a link that lifts on
// hover; a not-yet store is dimmed and non-interactive (same shape, clearly "soon").
type StoreBadgeProps = {
  glyph: React.ReactNode;
  top: string;
  name: string;
  href?: string;
};

const StoreBadge = ({ glyph, top, name, href }: StoreBadgeProps) => {
  const available = Boolean(href);
  return (
    <Flex
      as={available ? 'a' : 'div'}
      {...(available ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
      alignItems="center"
      gap={2.5}
      px={4}
      h="52px"
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.200"
      bg="whiteAlpha.50"
      opacity={available ? 1 : 0.55}
      cursor={available ? 'pointer' : 'default'}
      transition="all 0.2s"
      _hover={
        available ? { transform: 'translateY(-2px)', borderColor: 'whiteAlpha.300' } : undefined
      }
    >
      <Box color="fg.emphasized" flexShrink={0} display="flex">
        {glyph}
      </Box>
      <Flex direction="column" lineHeight="1.15" alignItems="flex-start">
        <Text fontSize="2xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.04em">
          {top}
        </Text>
        <Text fontSize="md" fontWeight="700" color="fg.emphasized" whiteSpace="nowrap">
          {name}
        </Text>
      </Flex>
    </Flex>
  );
};

export const MobileApp = () => {
  const { t } = useLingui();

  return (
    <Box as="section" aria-label="Mobile App" w="100%">
      <Flex
        direction="column"
        alignItems="center"
        gap={6}
        p={{ base: 8, md: 12 }}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 14%, 0.6) 0%, hsla(228, 11.6%, 10%, 0.4) 100%)"
        textAlign="center"
        asChild
      >
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            color="purple.300"
            textTransform="uppercase"
            letterSpacing="0.15em"
          >
            {t`v2.mobileApp.label`}
          </Text>
          <Heading fontWeight="900" fontSize={{ base: 'xl', md: '2xl' }} lineHeight="1.2" as="h2">
            {t`v2.mobileApp.title`}
          </Heading>
          <Text fontSize="sm" color="fg.muted" lineHeight="1.7" maxW="480px">
            {t`v2.mobileApp.description`}
          </Text>

          <Flex gap={3} flexWrap="wrap" justifyContent="center" alignItems="center" mt={2}>
            <StoreBadge
              glyph={<GooglePlayGlyph />}
              top={t`v2.mobileApp.availableOn`}
              name="Google Play"
              href={playStoreUrl('landing-section')}
            />
            <StoreBadge
              glyph={<AppleGlyph />}
              top={t`v2.mobileApp.comingSoonTo`}
              name="App Store"
            />
          </Flex>
        </m.div>
      </Flex>
    </Box>
  );
};
